import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../config/api'
import { Button, Card, Badge, Alert, PageLayout, Tabs } from '../components/ui'
import CsvOptionImport from '../components/CsvOptionImport'
import TranslationManager from '../components/TranslationManager'
import TranslationReview from '../components/TranslationReview'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableSyntheticListeners,
  type DraggableAttributes,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { SURVEY_GROUP_PALETTE } from '../styles/colorContrast'

// ---------- Info Tooltip ----------

function InfoTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false)
  return (
    <span className="relative inline-block ml-1 align-middle">
      <span
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-border text-foreground-muted text-xs cursor-help select-none"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        i
      </span>
      {show && (
        <span className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-foreground rounded-lg shadow-lg whitespace-normal w-56 text-center pointer-events-none">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-foreground" />
        </span>
      )}
    </span>
  )
}

// ---------- Types ----------

interface QuestionGroup {
  id: string
  name: string
  color: string
  description: string
  order: number
}

interface ScoringConfig {
  type: string
  true_value?: number
  false_value?: number
  min_value?: number
  max_value?: number
  option_weights?: Record<string, number>
}

interface BuilderQuestion {
  id: string
  text: string
  type: 'BOOLEAN' | 'TEXT' | 'RATING' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'COUNTRY'
  required: boolean
  options: string[]
  groupId: string | null
  order: number
  scoringEnabled: boolean
  scoringConfig: ScoringConfig
  ratingLabels?: Record<string, string>
  weight: number
}

interface Organization {
  id: string
  name: string
}

// ---------- Constants ----------

const SURVEY_TYPE_OPTIONS = [
  { value: 'Impact Assessment', labelId: 'builder.surveyType.impact.label', descId: 'builder.surveyType.impact.desc' },
  { value: 'Needs Assessment', labelId: 'builder.surveyType.needs.label', descId: 'builder.surveyType.needs.desc' },
  { value: 'Program Feedback', labelId: 'builder.surveyType.feedback.label', descId: 'builder.surveyType.feedback.desc' },
  { value: 'Pre/Post Evaluation', labelId: 'builder.surveyType.prepost.label', descId: 'builder.surveyType.prepost.desc' },
  { value: 'Custom', labelId: 'builder.surveyType.custom.label', descId: 'builder.surveyType.custom.desc' },
]

const RESULT_TYPE_OPTIONS = [
  { value: 'flat', labelId: 'builder.resultType.flat.label', descId: 'builder.resultType.flat.desc' },
  { value: 'breakdown', labelId: 'builder.resultType.breakdown.label', descId: 'builder.resultType.breakdown.desc' },
  { value: 'pyramid', labelId: 'builder.resultType.pyramid.label', descId: 'builder.resultType.pyramid.desc' },
  { value: 'radar', labelId: 'builder.resultType.radar.label', descId: 'builder.resultType.radar.desc' },
]

const QUESTION_TYPE_OPTIONS = [
  { value: 'RATING', labelId: 'builder.questionType.rating' },
  { value: 'BOOLEAN', labelId: 'builder.questionType.boolean' },
  { value: 'SINGLE_CHOICE', labelId: 'builder.questionType.singleChoice' },
  { value: 'MULTIPLE_CHOICE', labelId: 'builder.questionType.multipleChoice' },
  { value: 'COUNTRY', labelId: 'builder.questionType.country' },
  { value: 'TEXT', labelId: 'builder.questionType.text' },
]

const DEFAULT_RATING_LABELS: Record<string, string> = {
  '0': 'N/A',
  '1': 'Poor',
  '2': 'Fair',
  '3': 'Good',
  '4': 'Very Good',
  '5': 'Excellent',
}

let _idCounter = 0
function genId(prefix: string) {
  return `${prefix}_${Date.now()}_${++_idCounter}`
}

// ---------- Component ----------

const SurveyBuilderPage: React.FC = () => {
  useAuth()
  const intl = useIntl()
  const navigate = useNavigate()
  const { surveyId } = useParams<{ surveyId: string }>()
  const [searchParams] = useSearchParams()
  const isEditing = !!surveyId

  // Survey details
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [organizationId, setOrganizationId] = useState(searchParams.get('org') || '')
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [surveyType, setSurveyType] = useState('Impact Assessment')
  const [resultType, setResultType] = useState('flat')
  const [requireEmail, setRequireEmail] = useState(true)
  const [minDaysBetweenAttempts, setMinDaysBetweenAttempts] = useState(30)
  const [autoRemind, setAutoRemind] = useState(true)
  const [showSettings, setShowSettings] = useState(false)

  // Builder state
  const [groups, setGroups] = useState<QuestionGroup[]>([])
  const [questions, setQuestions] = useState<BuilderQuestion[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedType, setSelectedType] = useState<'question' | 'group'>('question')
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({})

  // UI state
  const [loading, setLoading] = useState(isEditing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [surveyStatus, setSurveyStatus] = useState<'DRAFT' | 'PUBLISHED'>('DRAFT')
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const initialLoadDone = useRef(false)

  // Translation state
  const [builderTab, setBuilderTab] = useState<'questions' | 'languages'>('questions')
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(['en'])
  const [translationMetadata, setTranslationMetadata] = useState<Record<string, any>>({})
  const [reviewLang, setReviewLang] = useState<string | null>(null)
  const [surveyDataForReview, setSurveyDataForReview] = useState<any>(null)

  // Load organizations
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const res = await fetch(API_ENDPOINTS.organizations.base, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        })
        if (res.ok) {
          const data = await res.json()
          setOrganizations(data)
          const orgFromUrl = searchParams.get('org')
          if (orgFromUrl && data.some((o: Organization) => o.id === orgFromUrl)) {
            setOrganizationId(orgFromUrl)
          } else if (data.length === 1) {
            setOrganizationId(data[0].id)
          }
        }
      } catch (err) {
        console.error('Failed to fetch organizations:', err)
      }
    }
    fetchOrgs()
  }, [])

  // Load existing survey if editing
  useEffect(() => {
    if (!surveyId) return
    const fetchSurvey = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const res = await fetch(API_ENDPOINTS.surveys.byId(surveyId), {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        })
        if (!res.ok) { setError(intl.formatMessage({ id: 'builder.error.surveyNotFound' })); return }
        const data = await res.json()

        setTitle(data.title)
        setDescription(data.description || '')
        setOrganizationId(data.organization_id || '')
        setSurveyType(data.settings?.survey_type || 'Impact Assessment')
        setResultType(data.result_type || 'flat')
        setRequireEmail(data.require_email ?? true)
        setMinDaysBetweenAttempts(data.min_days_between_attempts ?? 30)
        setAutoRemind(data.auto_remind ?? true)
        setSurveyStatus(data.status || 'DRAFT')
        setAvailableLanguages(data.available_languages || ['en'])
        setTranslationMetadata(data.translation_metadata || {})

        // Load groups
        const loadedGroups: QuestionGroup[] = (data.question_groups || []).map((g: any) => ({
          id: g.id,
          name: g.name,
          color: g.color,
          description: g.description || '',
          order: g.order,
        }))
        setGroups(loadedGroups)

        // Load questions
        const loadedQuestions: BuilderQuestion[] = (data.questions || []).map((q: any) => ({
          id: q.id,
          text: q.text,
          type: q.type,
          required: q.required,
          options: q.options || [],
          groupId: q.group_id || null,
          order: q.order,
          scoringEnabled: !!q.scoring_config,
          scoringConfig: q.scoring_config || { type: '' },
          ratingLabels: q.rating_labels,
          weight: q.weight ?? 1,
        }))
        setQuestions(loadedQuestions)
        if (loadedQuestions.length > 0) {
          setSelectedId(loadedQuestions[0].id)
          setSelectedType('question')
        }
      } catch (err) {
        console.error('Error loading survey:', err)
        setError(intl.formatMessage({ id: 'builder.error.loadFailed' }))
      } finally {
        setLoading(false)
        setTimeout(() => { initialLoadDone.current = true }, 500)
      }
    }
    fetchSurvey()
  }, [surveyId])

  // ---------- Question Management ----------

  const addQuestion = useCallback((groupId: string | null) => {
    const siblings = questions.filter((q) => q.groupId === groupId)
    const order = siblings.length > 0 ? Math.max(...siblings.map((q) => q.order)) + 1 : 0
    const newQ: BuilderQuestion = {
      id: genId('q'),
      text: '',
      type: 'RATING',
      required: true,
      options: [],
      groupId,
      order,
      scoringEnabled: true,
      scoringConfig: { type: 'direct_scale', min_value: 0, max_value: 5 },
      ratingLabels: { ...DEFAULT_RATING_LABELS },
      weight: 1,
    }
    setQuestions((prev) => [...prev, newQ])
    setSelectedId(newQ.id)
    setSelectedType('question')
  }, [questions])

  const updateQuestion = useCallback((id: string, updates: Partial<BuilderQuestion>) => {
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, ...updates } : q)))
  }, [])

  const removeQuestion = useCallback((id: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id))
    if (selectedId === id) setSelectedId(null)
  }, [selectedId])


  const moveQuestionToGroup = useCallback((questionId: string, newGroupId: string | null) => {
    setQuestions((prev) => {
      const siblings = prev.filter((q) => q.groupId === newGroupId)
      const order = siblings.length > 0 ? Math.max(...siblings.map((q) => q.order)) + 1 : 0
      return prev.map((q) => (q.id === questionId ? { ...q, groupId: newGroupId, order } : q))
    })
  }, [])

  // ---------- Group Management ----------

  const addGroup = useCallback(() => {
    const usedColors = groups.map((g) => g.color)
    const nextColor = SURVEY_GROUP_PALETTE.find((c) => !usedColors.includes(c.value))?.value || SURVEY_GROUP_PALETTE[0].value
    const order = groups.length > 0 ? Math.max(...groups.map((g) => g.order)) + 1 : 0
    const newG: QuestionGroup = {
      id: genId('g'),
      name: intl.formatMessage({ id: 'builder.defaultGroupName' }, { num: groups.length + 1 }),
      color: nextColor,
      description: '',
      order,
    }
    setGroups((prev) => [...prev, newG])
    setSelectedId(newG.id)
    setSelectedType('group')
  }, [groups, intl])

  const updateGroup = useCallback((id: string, updates: Partial<QuestionGroup>) => {
    setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...updates } : g)))
  }, [])

  const removeGroup = useCallback((id: string) => {
    // Move questions to ungrouped
    setQuestions((prev) => prev.map((q) => (q.groupId === id ? { ...q, groupId: null } : q)))
    setGroups((prev) => prev.filter((g) => g.id !== id))
    if (selectedId === id) setSelectedId(null)
  }, [selectedId])


  const toggleGroupCollapse = useCallback((id: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  // ---------- Type Change Handler ----------

  const handleTypeChange = useCallback((id: string, newType: BuilderQuestion['type']) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== id) return q
        const updates: Partial<BuilderQuestion> = { type: newType }

        const hasOptions = newType === 'SINGLE_CHOICE' || newType === 'MULTIPLE_CHOICE'
        if (!hasOptions) {
          updates.options = []
        } else if (q.options.length === 0) {
          updates.options = [
            intl.formatMessage({ id: 'builder.defaultOption' }, { num: 1 }),
            intl.formatMessage({ id: 'builder.defaultOption' }, { num: 2 }),
          ]
        }

        if (q.scoringEnabled) {
          switch (newType) {
            case 'RATING':
              updates.scoringConfig = { type: 'direct_scale', min_value: 0, max_value: 5 }
              updates.ratingLabels = q.ratingLabels || { ...DEFAULT_RATING_LABELS }
              break
            case 'BOOLEAN':
              updates.scoringConfig = { type: 'binary', true_value: 10, false_value: 0 }
              break
            case 'SINGLE_CHOICE':
            case 'MULTIPLE_CHOICE': {
              const opts = updates.options || q.options
              const weights: Record<string, number> = {}
              opts.forEach((o, i) => { weights[o] = opts.length - i })
              updates.scoringConfig = { type: 'weighted_options', option_weights: weights }
              break
            }
            case 'TEXT':
              updates.scoringConfig = { type: 'qualitative' }
              break
            case 'COUNTRY':
              updates.scoringConfig = { type: 'qualitative' }
              break
          }
        }

        return { ...q, ...updates }
      })
    )
  }, [intl])

  // ---------- Save / Publish ----------

  const handleSave = async (publish: boolean) => {
    if (!organizationId) { setError(intl.formatMessage({ id: 'builder.error.selectOrganization' })); return }
    if (!title.trim()) { setError(intl.formatMessage({ id: 'builder.error.titleRequired' })); return }
    if (questions.length === 0) { setError(intl.formatMessage({ id: 'builder.error.addQuestion' })); return }
    if (questions.some((q) => !q.text.trim())) { setError(intl.formatMessage({ id: 'builder.error.questionsNeedText' })); return }

    setSaving(true)
    setError('')

    try {
      const token = localStorage.getItem('access_token')
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      }

      // Build groups for API
      const apiGroups = groups.length > 0
        ? groups.map((g) => ({
            id: g.id,
            name: g.name,
            order: g.order,
            color: g.color,
            description: g.description || undefined,
          }))
        : undefined

      // Build questions for API
      const apiQuestions = questions.map((q, i) => ({
        text: q.text,
        type: q.type,
        required: q.required,
        order: i,
        options: q.options,
        group_id: q.groupId || undefined,
        weight: q.weight,
        rating_labels: q.type === 'RATING' ? q.ratingLabels : undefined,
        scoring_config: q.scoringEnabled && q.scoringConfig.type
          ? q.scoringConfig
          : undefined,
      }))

      if (isEditing) {
        // Update survey metadata
        await fetch(API_ENDPOINTS.surveys.byId(surveyId!), {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            title,
            description,
            settings: { survey_type: surveyType },
            result_type: resultType,
            require_email: requireEmail,
            min_days_between_attempts: minDaysBetweenAttempts,
            auto_remind: autoRemind,
            question_groups: apiGroups || null,
            ...(publish ? { status: 'PUBLISHED' } : {}),
          }),
        })

        // Delete old questions and recreate
        const existingRes = await fetch(API_ENDPOINTS.surveys.byId(surveyId!), {
          headers,
        })
        const existingData = await existingRes.json()
        for (const eq of existingData.questions || []) {
          await fetch(`${API_ENDPOINTS.surveys.byId(surveyId!)}/questions/${eq.id}`, {
            method: 'DELETE',
            headers,
          }).catch(() => {})
        }

        // Create new questions
        for (const q of apiQuestions) {
          await fetch(API_ENDPOINTS.surveys.questions(surveyId!), {
            method: 'POST',
            headers,
            body: JSON.stringify(q),
          })
        }

        navigate(`/surveys/${surveyId}/share`)
      } else {
        // Create new survey
        const surveyRes = await fetch(API_ENDPOINTS.surveys.base, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            title,
            description,
            organization_id: organizationId,
            settings: { survey_type: surveyType },
            result_type: resultType,
            require_email: requireEmail,
            min_days_between_attempts: minDaysBetweenAttempts,
            auto_remind: autoRemind,
            question_groups: apiGroups,
            questions: apiQuestions,
          }),
        })

        if (!surveyRes.ok) throw new Error('Failed to create survey')
        const survey = await surveyRes.json()

        if (publish) {
          await fetch(API_ENDPOINTS.surveys.byId(survey.id), {
            method: 'PUT',
            headers,
            body: JSON.stringify({ status: 'PUBLISHED' }),
          })
        }

        navigate(`/surveys/${survey.id}/share`)
      }
    } catch (err) {
      console.error('Error saving survey:', err)
      setError(intl.formatMessage({ id: 'builder.error.saveFailed' }))
    } finally {
      setSaving(false)
    }
  }

  // ---------- Auto-Save (editing only, metadata + groups ONLY) ----------

  const autoSave = useCallback(async () => {
    if (!isEditing || !surveyId || !title.trim()) return

    setSaveStatus('saving')
    try {
      const token = localStorage.getItem('access_token')
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      }

      const apiGroups = groups.length > 0
        ? groups.map((g) => ({ id: g.id, name: g.name, order: g.order, color: g.color, description: g.description || undefined }))
        : undefined

      await fetch(API_ENDPOINTS.surveys.byId(surveyId), {
        method: 'PUT', headers,
        body: JSON.stringify({
          title, description, settings: { survey_type: surveyType }, result_type: resultType,
          require_email: requireEmail, min_days_between_attempts: minDaysBetweenAttempts,
          auto_remind: autoRemind, question_groups: apiGroups || null,
        }),
      })

      setSaveStatus('saved')
      setTimeout(() => setSaveStatus((s) => s === 'saved' ? 'idle' : s), 2000)
    } catch (err) {
      console.error('Auto-save failed:', err)
      setSaveStatus('error')
    }
  }, [isEditing, surveyId, title, description, surveyType, resultType, requireEmail, minDaysBetweenAttempts, autoRemind, groups])

  // Trigger auto-save on metadata/group changes only (not questions)
  useEffect(() => {
    if (!isEditing || !initialLoadDone.current) return
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = setTimeout(() => { autoSave() }, 1500)
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current) }
  }, [title, description, surveyType, resultType, requireEmail, minDaysBetweenAttempts, autoRemind, groups, autoSave])

  // ---------- Computed ----------

  const sortedGroups = [...groups].sort((a, b) => a.order - b.order)
  const ungroupedQuestions = questions
    .filter((q) => !q.groupId)
    .sort((a, b) => a.order - b.order)
  const selectedQuestion = selectedType === 'question' ? questions.find((q) => q.id === selectedId) : null
  const selectedGroup = selectedType === 'group' ? groups.find((g) => g.id === selectedId) : null
  const scoredCount = questions.filter((q) => q.scoringEnabled).length

  // ---------- Drag & Drop ----------

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const groupIds = useMemo(() => sortedGroups.map((g) => g.id), [sortedGroups])

  const handleGroupDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = groups.findIndex((g) => g.id === active.id)
    const newIdx = groups.findIndex((g) => g.id === over.id)
    if (oldIdx === -1 || newIdx === -1) return
    setGroups((prev) => {
      const updated = [...prev]
      const [moved] = updated.splice(oldIdx, 1)
      updated.splice(newIdx, 0, moved)
      return updated.map((g, i) => ({ ...g, order: i }))
    })
  }, [groups])

  const handleQuestionDragEnd = useCallback((groupId: string | null) => (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setQuestions((prev) => {
      const inGroup = prev.filter((q) => (groupId ? q.groupId === groupId : !q.groupId))
      const rest = prev.filter((q) => (groupId ? q.groupId !== groupId : !!q.groupId))
      const oldIdx = inGroup.findIndex((q) => q.id === active.id)
      const newIdx = inGroup.findIndex((q) => q.id === over.id)
      if (oldIdx === -1 || newIdx === -1) return prev
      const [moved] = inGroup.splice(oldIdx, 1)
      inGroup.splice(newIdx, 0, moved)
      const reordered = inGroup.map((q, i) => ({ ...q, order: i }))
      return [...rest, ...reordered]
    })
  }, [])

  // ---------- Render Helpers ----------

  const questionsInGroup = (groupId: string) =>
    questions.filter((q) => q.groupId === groupId).sort((a, b) => a.order - b.order)

  const typeLabel = (type: string) => {
    const opt = QUESTION_TYPE_OPTIONS.find((o) => o.value === type)
    return opt ? intl.formatMessage({ id: opt.labelId }) : type
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground-muted"><FormattedMessage id="builder.loading" /></div>
      </div>
    )
  }

  return (
    <PageLayout maxWidth="xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground"><FormattedMessage id="builder.title" /></h1>
            {surveyStatus === 'DRAFT' && <Badge variant="warning"><FormattedMessage id="builder.status.draft" /></Badge>}
            {surveyStatus === 'PUBLISHED' && <Badge variant="success"><FormattedMessage id="builder.status.published" /></Badge>}
            {isEditing && (
              <span className="text-xs text-foreground-muted">
                {saveStatus === 'saving' && intl.formatMessage({ id: 'builder.saveStatus.saving' })}
                {saveStatus === 'saved' && intl.formatMessage({ id: 'builder.saveStatus.saved' })}
                {saveStatus === 'error' && intl.formatMessage({ id: 'builder.saveStatus.error' })}
              </span>
            )}
          </div>
          <p className="text-sm text-foreground-muted mt-1"><FormattedMessage id="builder.subtitle" /></p>
        </div>
        <div className="flex gap-2">
          {isEditing && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/surveys/${surveyId}/test`)}>
              <FormattedMessage id="builder.testSurvey" />
            </Button>
          )}
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={() => handleSave(false)} loading={saving}>
              <FormattedMessage id="builder.saveDraft" />
            </Button>
          )}
          {surveyStatus === 'DRAFT' && (
            <Button variant="primary" size="sm" onClick={() => handleSave(true)} loading={saving}>
              <FormattedMessage id="builder.publish" />
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      {/* Survey Details Card */}
      <Card className="mb-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Row 1 */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1"><FormattedMessage id="builder.field.title" /></label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={intl.formatMessage({ id: 'builder.placeholder.title' })}
              className="block w-full px-3 py-2 rounded border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-1"><FormattedMessage id="builder.field.organization" /></label>
              <select
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                className="block w-full px-3 py-2 rounded border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
              >
                <option value="">{intl.formatMessage({ id: 'builder.select.placeholder' })}</option>
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-1">
                <FormattedMessage id="builder.field.surveyType" />
                <InfoTooltip text={(() => { const o = SURVEY_TYPE_OPTIONS.find(o => o.value === surveyType); return o ? intl.formatMessage({ id: o.descId }) : intl.formatMessage({ id: 'builder.surveyType.tooltipFallback' }) })()} />
              </label>
              <select
                value={surveyType}
                onChange={(e) => setSurveyType(e.target.value)}
                className="block w-full px-3 py-2 rounded border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
              >
                {SURVEY_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{intl.formatMessage({ id: o.labelId })}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2 */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1"><FormattedMessage id="builder.field.description" /></label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={intl.formatMessage({ id: 'builder.placeholder.description' })}
              rows={2}
              className="block w-full px-3 py-2 rounded border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
            />
          </div>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-1">
                <FormattedMessage id="builder.field.resultVisualization" />
                <InfoTooltip text={(() => { const o = RESULT_TYPE_OPTIONS.find(o => o.value === resultType); return o ? intl.formatMessage({ id: o.descId }) : intl.formatMessage({ id: 'builder.resultType.tooltipFallback' }) })()} />
              </label>
              <select
                value={resultType}
                onChange={(e) => setResultType(e.target.value)}
                className="block w-full px-3 py-2 rounded border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
              >
                {RESULT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{intl.formatMessage({ id: o.labelId })}</option>
                ))}
              </select>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
            >
              {showSettings
                ? intl.formatMessage({ id: 'builder.settings.hide' })
                : intl.formatMessage({ id: 'builder.settings.more' })}
            </Button>
          </div>
        </div>

        {/* Expandable Settings */}
        {showSettings && (
          <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={requireEmail}
                onChange={(e) => setRequireEmail(e.target.checked)}
                className="h-4 w-4 text-primary border-border rounded mr-2"
              />
              <span className="text-sm text-foreground"><FormattedMessage id="builder.settings.requireEmail" /></span>
            </label>
            <div className="flex items-center gap-2">
              <label className="text-sm text-foreground"><FormattedMessage id="builder.settings.daysBetweenRetakes" /></label>
              <input
                type="number"
                value={minDaysBetweenAttempts}
                onChange={(e) => setMinDaysBetweenAttempts(Math.max(0, parseInt(e.target.value) || 0))}
                min={0}
                max={365}
                className="w-20 px-2 py-1 rounded border border-border text-foreground bg-surface sm:text-sm"
              />
              <span className="text-xs text-foreground-muted"><FormattedMessage id="builder.settings.noLimitHint" /></span>
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={autoRemind}
                onChange={(e) => setAutoRemind(e.target.checked)}
                className="h-4 w-4 text-primary border-border rounded mr-2"
              />
              <span className="text-sm text-foreground"><FormattedMessage id="builder.settings.autoRemind" /></span>
            </label>
          </div>
        )}
      </Card>

      {/* Tab bar for Questions / Languages */}
      {isEditing && (
        <div className="mb-4">
          <Tabs
            tabs={[
              { key: 'questions', label: intl.formatMessage({ id: 'builder.tab.questions' }) },
              {
                key: 'languages',
                label: availableLanguages.length > 1
                  ? intl.formatMessage({ id: 'builder.tab.languagesCount' }, { count: availableLanguages.length - 1 })
                  : intl.formatMessage({ id: 'builder.tab.languages' }),
              },
            ]}
            activeTab={builderTab}
            onChange={(key) => setBuilderTab(key as 'questions' | 'languages')}
          />
        </div>
      )}

      {/* Languages Tab */}
      {builderTab === 'languages' && isEditing && surveyId && (
        <TranslationManager
          surveyId={surveyId}
          availableLanguages={availableLanguages}
          translationMetadata={translationMetadata}
          onLanguagesChanged={async () => {
            // Re-fetch survey to get updated translation data
            const token = localStorage.getItem('access_token')
            const res = await fetch(API_ENDPOINTS.surveys.byId(surveyId), {
              headers: { Authorization: token ? `Bearer ${token}` : '' },
            })
            if (res.ok) {
              const data = await res.json()
              setAvailableLanguages(data.available_languages || ['en'])
              setTranslationMetadata(data.translation_metadata || {})
            }
          }}
          onReview={async (lang) => {
            // Fetch full survey data for review modal
            const token = localStorage.getItem('access_token')
            const res = await fetch(API_ENDPOINTS.surveys.byId(surveyId), {
              headers: { Authorization: token ? `Bearer ${token}` : '' },
            })
            if (res.ok) {
              const data = await res.json()
              setSurveyDataForReview(data)
              setReviewLang(lang)
            }
          }}
        />
      )}

      {/* Translation Review Modal */}
      {reviewLang && surveyDataForReview && surveyId && (
        <TranslationReview
          surveyId={surveyId}
          lang={reviewLang}
          survey={surveyDataForReview}
          onSave={async () => {
            setReviewLang(null)
            setSurveyDataForReview(null)
            // Re-fetch survey data
            const token = localStorage.getItem('access_token')
            const res = await fetch(API_ENDPOINTS.surveys.byId(surveyId), {
              headers: { Authorization: token ? `Bearer ${token}` : '' },
            })
            if (res.ok) {
              const data = await res.json()
              setAvailableLanguages(data.available_languages || ['en'])
              setTranslationMetadata(data.translation_metadata || {})
            }
          }}
          onClose={() => { setReviewLang(null); setSurveyDataForReview(null) }}
        />
      )}

      {/* Main Two-Panel Layout */}
      <div className="grid grid-cols-12 gap-4" style={{ minHeight: 600, display: builderTab === 'questions' ? undefined : 'none' }}>
        {/* LEFT: Question Outline */}
        <div className="col-span-4">
          <Card className="h-full flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-foreground"><FormattedMessage id="builder.panel.questions" /></h3>
              <Badge variant="neutral">{questions.length}</Badge>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Ungrouped Questions */}
              {ungroupedQuestions.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-bold text-foreground-muted mb-1"><FormattedMessage id="builder.ungrouped" /></p>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleQuestionDragEnd(null)}>
                    <SortableContext items={ungroupedQuestions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                      {ungroupedQuestions.map((q) => (
                        <SortableQuestion
                          key={q.id}
                          q={q}
                          selected={selectedId === q.id && selectedType === 'question'}
                          onSelect={() => { setSelectedId(q.id); setSelectedType('question') }}
                          typeLabel={typeLabel(q.type)}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {/* Groups */}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGroupDragEnd}>
                <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
                  {sortedGroups.map((g) => {
                    const gQuestions = questionsInGroup(g.id)
                    const collapsed = collapsedGroups[g.id]
                    return (
                      <SortableGroup
                        key={g.id}
                        g={g}
                        selected={selectedId === g.id && selectedType === 'group'}
                        collapsed={!!collapsed}
                        onSelect={() => { setSelectedId(g.id); setSelectedType('group') }}
                        onToggleCollapse={() => toggleGroupCollapse(g.id)}
                        questionCount={gQuestions.length}
                      >
                        {!collapsed && (
                          <div className="ml-4">
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleQuestionDragEnd(g.id)}>
                              <SortableContext items={gQuestions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                                {gQuestions.map((q) => (
                                  <SortableQuestion
                                    key={q.id}
                                    q={q}
                                    selected={selectedId === q.id && selectedType === 'question'}
                                    onSelect={() => { setSelectedId(q.id); setSelectedType('question') }}
                                    typeLabel={typeLabel(q.type)}
                                    borderColor={g.color}
                                  />
                                ))}
                              </SortableContext>
                            </DndContext>
                            <button
                              className="text-xs text-primary hover:text-primary-dark px-2 py-1"
                              style={{ borderLeft: `2px solid ${g.color}` }}
                              onClick={() => addQuestion(g.id)}
                            >
                              <FormattedMessage id="builder.addQuestionShort" />
                            </button>
                          </div>
                        )}
                      </SortableGroup>
                    )
                  })}
                </SortableContext>
              </DndContext>
            </div>

            {/* Bottom actions */}
            <div className="border-t border-border mt-3 pt-3 flex gap-2">
              <button
                className="text-sm text-primary hover:text-primary-dark font-medium"
                onClick={() => addQuestion(null)}
              >
                <FormattedMessage id="builder.addQuestionShort" />
              </button>
              <button
                className="text-sm text-primary hover:text-primary-dark font-medium"
                onClick={addGroup}
              >
                <FormattedMessage id="builder.addGroupShort" />
              </button>
            </div>
          </Card>
        </div>

        {/* RIGHT: Editor Panel */}
        <div className="col-span-8">
          {!selectedId && (
            <Card className="h-full flex items-center justify-center">
              <p className="text-foreground-muted text-sm"><FormattedMessage id="builder.emptyState" /></p>
            </Card>
          )}

          {/* Question Editor */}
          {selectedQuestion && (
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground"><FormattedMessage id="builder.editQuestion" /></h3>
                <div className="flex items-center gap-2">
                  {selectedQuestion.required && <Badge variant="warning" size="sm"><FormattedMessage id="builder.required" /></Badge>}
                  <Button variant="danger" size="sm" onClick={() => removeQuestion(selectedQuestion.id)}>
                    <FormattedMessage id="builder.delete" />
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {/* Question text */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1"><FormattedMessage id="builder.field.questionText" /></label>
                  <textarea
                    value={selectedQuestion.text}
                    onChange={(e) => updateQuestion(selectedQuestion.id, { text: e.target.value })}
                    rows={2}
                    placeholder={intl.formatMessage({ id: 'builder.placeholder.questionText' })}
                    className="block w-full px-3 py-2 rounded border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
                  />
                </div>

                {/* Type, Group, Required */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1"><FormattedMessage id="builder.field.type" /></label>
                    <select
                      value={selectedQuestion.type}
                      onChange={(e) => handleTypeChange(selectedQuestion.id, e.target.value as BuilderQuestion['type'])}
                      className="block w-full px-3 py-2 rounded border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
                    >
                      {QUESTION_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{intl.formatMessage({ id: o.labelId })}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1"><FormattedMessage id="builder.field.group" /></label>
                    <select
                      value={selectedQuestion.groupId || ''}
                      onChange={(e) => moveQuestionToGroup(selectedQuestion.id, e.target.value || null)}
                      className="block w-full px-3 py-2 rounded border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
                    >
                      <option value="">{intl.formatMessage({ id: 'builder.ungroupedOption' })}</option>
                      {sortedGroups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col justify-end">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedQuestion.required}
                        onChange={(e) => updateQuestion(selectedQuestion.id, { required: e.target.checked })}
                        className="h-4 w-4 text-primary border-border rounded mr-2"
                      />
                      <span className="text-sm text-foreground"><FormattedMessage id="builder.required" /></span>
                    </label>
                  </div>
                </div>

                {/* Rating config */}
                {selectedQuestion.type === 'RATING' && (
                  <Card className="bg-background">
                    <h4 className="text-sm font-semibold text-foreground mb-3"><FormattedMessage id="builder.rating.title" /></h4>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        {[0, 1, 2, 3, 4, 5].map((n) => (
                          <div
                            key={n}
                            className="w-10 h-10 flex items-center justify-center rounded border-2 border-border text-sm font-bold text-foreground"
                          >
                            {n}
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between text-xs text-foreground-muted">
                        <span>{DEFAULT_RATING_LABELS['0']}</span>
                        <span>{DEFAULT_RATING_LABELS['5']}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-foreground"><FormattedMessage id="builder.weight" /></label>
                        <input
                          type="number"
                          value={selectedQuestion.weight}
                          onChange={(e) => updateQuestion(selectedQuestion.id, { weight: parseFloat(e.target.value) || 0 })}
                          onBlur={(e) => {
                            const v = parseFloat(e.target.value) || 1
                            updateQuestion(selectedQuestion.id, { weight: Math.min(10, Math.max(0.1, v)) })
                          }}
                          min={0.1}
                          max={10}
                          step={0.1}
                          className="w-20 px-2 py-1 rounded border border-border text-foreground bg-surface sm:text-sm"
                        />
                        <span className="text-xs text-foreground-muted"><FormattedMessage id="builder.weightHint" /></span>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Boolean config */}
                {selectedQuestion.type === 'BOOLEAN' && (
                  <Card className="bg-background">
                    <h4 className="text-sm font-semibold text-foreground mb-3"><FormattedMessage id="builder.boolean.title" /></h4>
                    <label className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        checked={selectedQuestion.scoringEnabled}
                        onChange={(e) => {
                          const enabled = e.target.checked
                          updateQuestion(selectedQuestion.id, {
                            scoringEnabled: enabled,
                            scoringConfig: enabled
                              ? { type: 'binary', true_value: 10, false_value: 0 }
                              : { type: '' },
                          })
                        }}
                        className="h-4 w-4 text-primary border-border rounded mr-2"
                      />
                      <span className="text-sm text-foreground"><FormattedMessage id="builder.enableScoring" /></span>
                    </label>
                    {selectedQuestion.scoringEnabled && (
                      <div className="flex gap-4">
                        <div>
                          <label className="text-xs text-foreground-muted"><FormattedMessage id="builder.boolean.pointsYes" /></label>
                          <input
                            type="number"
                            value={selectedQuestion.scoringConfig.true_value ?? 10}
                            onChange={(e) =>
                              updateQuestion(selectedQuestion.id, {
                                scoringConfig: {
                                  ...selectedQuestion.scoringConfig,
                                  true_value: parseFloat(e.target.value) || 0,
                                },
                              })
                            }
                            className="block w-20 px-2 py-1 rounded border border-border sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-foreground-muted"><FormattedMessage id="builder.boolean.pointsNo" /></label>
                          <input
                            type="number"
                            value={selectedQuestion.scoringConfig.false_value ?? 0}
                            onChange={(e) =>
                              updateQuestion(selectedQuestion.id, {
                                scoringConfig: {
                                  ...selectedQuestion.scoringConfig,
                                  false_value: parseFloat(e.target.value) || 0,
                                },
                              })
                            }
                            className="block w-20 px-2 py-1 rounded border border-border sm:text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </Card>
                )}

                {/* Choice options config (Single or Multiple) */}
                {(selectedQuestion.type === 'SINGLE_CHOICE' || selectedQuestion.type === 'MULTIPLE_CHOICE') && (
                  <Card className="bg-background">
                    <h4 className="text-sm font-semibold text-foreground mb-3">
                      {selectedQuestion.type === 'SINGLE_CHOICE'
                        ? intl.formatMessage({ id: 'builder.choice.singleTitle' })
                        : intl.formatMessage({ id: 'builder.choice.multipleTitle' })}
                    </h4>
                    <label className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        checked={selectedQuestion.scoringEnabled}
                        onChange={(e) => {
                          const enabled = e.target.checked
                          const weights: Record<string, number> = {}
                          if (enabled) {
                            selectedQuestion.options.forEach((o, i) => { weights[o] = selectedQuestion.options.length - i })
                          }
                          updateQuestion(selectedQuestion.id, {
                            scoringEnabled: enabled,
                            scoringConfig: enabled
                              ? { type: 'weighted_options', option_weights: weights }
                              : { type: '' },
                          })
                        }}
                        className="h-4 w-4 text-primary border-border rounded mr-2"
                      />
                      <span className="text-sm text-foreground"><FormattedMessage id="builder.enableScoring" /></span>
                    </label>
                    <div className="flex flex-col gap-2">
                      {selectedQuestion.options.map((opt, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="text-xs text-foreground-muted w-5">{idx + 1}.</span>
                          <input
                            type="text"
                            value={opt}
                            onChange={(e) => {
                              const newOptions = [...selectedQuestion.options]
                              const oldOpt = newOptions[idx]
                              newOptions[idx] = e.target.value
                              const updates: Partial<BuilderQuestion> = { options: newOptions }
                              if (selectedQuestion.scoringEnabled && selectedQuestion.scoringConfig.option_weights) {
                                const newWeights = { ...selectedQuestion.scoringConfig.option_weights }
                                const w = newWeights[oldOpt] ?? 0
                                delete newWeights[oldOpt]
                                newWeights[e.target.value] = w
                                updates.scoringConfig = { ...selectedQuestion.scoringConfig, option_weights: newWeights }
                              }
                              updateQuestion(selectedQuestion.id, updates)
                            }}
                            className="flex-1 px-2 py-1 rounded border border-border text-foreground bg-surface sm:text-sm"
                          />
                          {selectedQuestion.scoringEnabled && (
                            <input
                              type="number"
                              value={selectedQuestion.scoringConfig.option_weights?.[opt] ?? 0}
                              onChange={(e) => {
                                const newWeights = {
                                  ...(selectedQuestion.scoringConfig.option_weights || {}),
                                  [opt]: parseFloat(e.target.value) || 0,
                                }
                                updateQuestion(selectedQuestion.id, {
                                  scoringConfig: { ...selectedQuestion.scoringConfig, option_weights: newWeights },
                                })
                              }}
                              className="w-16 px-2 py-1 rounded border border-border sm:text-sm"
                              placeholder={intl.formatMessage({ id: 'builder.choice.ptsPlaceholder' })}
                            />
                          )}
                          {selectedQuestion.options.length > 2 && (
                            <button
                              className="text-error hover:text-error text-sm"
                              onClick={() => {
                                const newOptions = selectedQuestion.options.filter((_, i) => i !== idx)
                                const updates: Partial<BuilderQuestion> = { options: newOptions }
                                if (selectedQuestion.scoringEnabled && selectedQuestion.scoringConfig.option_weights) {
                                  const newWeights = { ...selectedQuestion.scoringConfig.option_weights }
                                  delete newWeights[opt]
                                  updates.scoringConfig = { ...selectedQuestion.scoringConfig, option_weights: newWeights }
                                }
                                updateQuestion(selectedQuestion.id, updates)
                              }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex items-start gap-4">
                      <button
                        className="text-sm text-primary hover:text-primary-dark"
                        onClick={() => {
                          const newOpt = intl.formatMessage({ id: 'builder.defaultOption' }, { num: selectedQuestion.options.length + 1 })
                          const newOptions = [...selectedQuestion.options, newOpt]
                          const updates: Partial<BuilderQuestion> = { options: newOptions }
                          if (selectedQuestion.scoringEnabled) {
                            updates.scoringConfig = {
                              ...selectedQuestion.scoringConfig,
                              option_weights: {
                                ...(selectedQuestion.scoringConfig.option_weights || {}),
                                [newOpt]: 0,
                              },
                            }
                          }
                          updateQuestion(selectedQuestion.id, updates)
                        }}
                      >
                        <FormattedMessage id="builder.addOption" />
                      </button>
                      <CsvOptionImport
                        existingOptionsCount={selectedQuestion.options.length}
                        onImport={(csvOptions, weights, mode) => {
                          const finalOptions = mode === 'append'
                            ? [...selectedQuestion.options, ...csvOptions.filter(o => !selectedQuestion.options.includes(o))]
                            : csvOptions
                          const updates: Partial<BuilderQuestion> = { options: finalOptions }
                          if (weights) {
                            const mergedWeights = mode === 'append'
                              ? { ...(selectedQuestion.scoringConfig.option_weights || {}), ...weights }
                              : weights
                            updates.scoringEnabled = true
                            updates.scoringConfig = {
                              type: 'weighted_options',
                              option_weights: mergedWeights,
                            }
                          } else if (selectedQuestion.scoringEnabled) {
                            const existingWeights = mode === 'append'
                              ? { ...(selectedQuestion.scoringConfig.option_weights || {}) }
                              : {}
                            finalOptions.forEach((o) => {
                              if (!(o in existingWeights)) existingWeights[o] = 0
                            })
                            updates.scoringConfig = {
                              ...selectedQuestion.scoringConfig,
                              option_weights: existingWeights,
                            }
                          }
                          updateQuestion(selectedQuestion.id, updates)
                        }}
                      />
                    </div>
                  </Card>
                )}

                {/* Text config */}
                {selectedQuestion.type === 'TEXT' && (
                  <Card className="bg-background">
                    <h4 className="text-sm font-semibold text-foreground mb-2"><FormattedMessage id="builder.text.title" /></h4>
                    <p className="text-sm text-foreground-muted"><FormattedMessage id="builder.text.description" /></p>
                  </Card>
                )}
              </div>
            </Card>
          )}

          {/* Group Editor */}
          {selectedGroup && (
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground"><FormattedMessage id="builder.editGroup" /></h3>
                <Button variant="danger" size="sm" onClick={() => removeGroup(selectedGroup.id)}>
                  <FormattedMessage id="builder.deleteGroup" />
                </Button>
              </div>

              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1"><FormattedMessage id="builder.field.groupName" /></label>
                  <input
                    value={selectedGroup.name}
                    onChange={(e) => updateGroup(selectedGroup.id, { name: e.target.value })}
                    className="block w-full px-3 py-2 rounded border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1"><FormattedMessage id="builder.field.description" /></label>
                  <textarea
                    value={selectedGroup.description}
                    onChange={(e) => updateGroup(selectedGroup.id, { description: e.target.value })}
                    rows={2}
                    className="block w-full px-3 py-2 rounded border border-border text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1"><FormattedMessage id="builder.field.color" /></label>
                  <div className="flex gap-2 mt-1">
                    {SURVEY_GROUP_PALETTE.map((c) => (
                      <button
                        key={c.value}
                        className="w-8 h-8 rounded"
                        style={{
                          backgroundColor: c.value,
                          border: selectedGroup.color === c.value
                            ? '3px solid black'
                            : '1px solid var(--theme-border-light, #e5e7eb)',
                        }}
                        title={c.label}
                        onClick={() => updateGroup(selectedGroup.id, { color: c.value })}
                      />
                    ))}
                  </div>
                </div>

                <div className="border-t border-border pt-3">
                  <p className="text-sm font-bold text-foreground mb-2">
                    <FormattedMessage id="builder.questionsInGroup" values={{ count: questionsInGroup(selectedGroup.id).length }} />
                  </p>
                  <Alert variant="info">
                    <FormattedMessage id="builder.groupInfo" />
                  </Alert>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Footer Summary */}
      <Card className="mt-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-6 text-sm text-foreground-muted">
            <span><FormattedMessage id="builder.summary.groups" values={{ count: groups.length, strong: (chunks) => <strong>{chunks}</strong> }} /></span>
            <span><FormattedMessage id="builder.summary.questions" values={{ count: questions.length, strong: (chunks) => <strong>{chunks}</strong> }} /></span>
            <span><FormattedMessage id="builder.summary.scored" values={{ count: scoredCount, strong: (chunks) => <strong>{chunks}</strong> }} /></span>
          </div>
          <div className="flex gap-2">
            {isEditing && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/surveys/${surveyId}/test`)}>
                <FormattedMessage id="builder.testSurvey" />
              </Button>
            )}
            {!isEditing && (
              <Button variant="outline" size="sm" onClick={() => handleSave(false)} loading={saving}>
                <FormattedMessage id="builder.saveDraft" />
              </Button>
            )}
            {surveyStatus === 'DRAFT' && (
              <Button variant="primary" size="sm" onClick={() => handleSave(true)} loading={saving}>
                <FormattedMessage id="builder.publish" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    </PageLayout>
  )
}

// ---------- Drag Handle ----------

const DragHandle = ({ listeners, attributes }: { listeners?: DraggableSyntheticListeners; attributes?: DraggableAttributes }) => (
  <span
    className="text-foreground-muted hover:text-foreground cursor-grab active:cursor-grabbing px-0.5 text-sm select-none"
    {...listeners}
    {...attributes}
  >
    ⠿
  </span>
)

// ---------- Sortable Group ----------

interface SortableGroupProps {
  g: QuestionGroup
  selected: boolean
  collapsed: boolean
  onSelect: () => void
  onToggleCollapse: () => void
  questionCount: number
  children: React.ReactNode
}

const SortableGroup: React.FC<SortableGroupProps> = ({ g, selected, collapsed, onSelect, onToggleCollapse, questionCount, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: g.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className="mb-3">
      <div
        className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer"
        style={{
          borderLeft: `4px solid ${g.color}`,
          backgroundColor: selected ? 'var(--theme-accent, rgba(99,102,241,0.1))' : undefined,
        }}
        onClick={onSelect}
      >
        <DragHandle listeners={listeners} attributes={attributes} />
        <button
          className="text-foreground-muted text-xs"
          onClick={(e) => { e.stopPropagation(); onToggleCollapse() }}
        >
          {collapsed ? '▶' : '▼'}
        </button>
        <span className="text-sm font-medium text-foreground flex-1 truncate" title={g.name}>{g.name}</span>
        <Badge variant="neutral" size="sm">{questionCount}</Badge>
      </div>
      {children}
    </div>
  )
}

// ---------- Sortable Question ----------

interface SortableQuestionProps {
  q: BuilderQuestion
  selected: boolean
  onSelect: () => void
  typeLabel: string
  borderColor?: string
}

const SortableQuestion: React.FC<SortableQuestionProps> = ({ q, selected, onSelect, typeLabel, borderColor }) => {
  const intl = useIntl()
  const untitled = intl.formatMessage({ id: 'builder.untitled' })
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        borderLeft: borderColor ? `2px solid ${borderColor}` : undefined,
        backgroundColor: selected ? 'var(--theme-accent, rgba(99,102,241,0.1))' : undefined,
      }}
      className="flex items-center gap-1 px-2 py-1 rounded cursor-pointer"
      onClick={onSelect}
    >
      <DragHandle listeners={listeners} attributes={attributes} />
      <span className="text-sm text-foreground flex-1 truncate" title={q.text || untitled}>{q.text || untitled}</span>
      <span className="text-xs text-foreground-muted bg-background-alt px-1.5 py-0.5 rounded whitespace-nowrap">
        {typeLabel}
      </span>
    </div>
  )
}

export default SurveyBuilderPage
